import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Shield, BookOpen, Lock, UserCheck, GraduationCap, ChevronDown, Mail, ArrowLeft, ArrowRight } from 'lucide-react';

const fadeIn = { hidden: { opacity: 0, y: 12 }, visible: { opacity: 1, y: 0 } };

const FAQS = [
  { q: 'Does istudyPRO generate essays for students?', a: 'No. Our platform focuses exclusively on study organisation — summaries, flashcards, SRS review, practice exams, and Q&A drills — not generating academic submissions. Students still do their own thinking and writing.' },
  { q: 'Can students record without my permission?', a: 'We strongly encourage students to seek lecturer consent. Our app includes a "Lecturer Consent" checkbox when creating a course folder, creating a timestamped record. You can request this log at any time.' },
  { q: 'Is my lecture content used to train AI models?', a: 'Absolutely not. We enforce a strict Zero-Training Policy. Your intellectual property is never used to improve foundation AI models. All processing is ephemeral and encrypted. Audio can be deleted immediately after transcription.' },
  { q: 'What data does istudyPRO collect from lectures?', a: 'Only the audio recording provided by the student. This is transcribed, summarised into flashcards and quiz questions, and then the raw audio can be deleted immediately (Ephemeral Mode). We never access your university systems.' },
  { q: 'How can I verify a student has consent logged?', a: 'Students who use the consent feature have a timestamped, immutable record in their profile. They can share this with you, or you can contact our Compliance Team.' },
  { q: 'What study tools do students get from recorded lectures?', a: 'From each lecture, students receive: AI transcription, smart summaries, auto-generated flashcards with spaced repetition scheduling, practice exam questions, and the ability to chat with an AI Study Buddy about the lecture content.' },
];

const PRINCIPLES = [
  { num: '01', icon: Lock, title: 'Zero-Training Policy', body: 'Your lectures are NEVER used to train foundation AI models. Audio is processed, transcribed, and (optionally) deleted on demand.' },
  { num: '02', icon: UserCheck, title: 'Consent-First', body: 'Students log lecturer consent at folder creation. Records are timestamped and shareable on request.' },
  { num: '03', icon: BookOpen, title: 'No essay generation', body: 'We build study tools (flashcards, SRS, practice quizzes) — not essay or assignment generators. Students still do the thinking.' },
  { num: '04', icon: Shield, title: 'GDPR & FERPA aligned', body: 'Encryption at rest, regional data residency, signed DPAs available for institutions on request.' },
];

export default function EducatorsPage() {
  const [openFaq, setOpenFaq] = useState(null);

  return (
    <div className="f500-page min-h-screen bg-[var(--isp-page)]" data-testid="educators-page">
      {/* Top bar */}
      <header className="border-b border-[var(--f500-line)] sticky top-0 z-40 bg-[var(--isp-page)]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-4 flex items-center justify-between">
          <Link to="/" className="inline-flex items-center gap-2 text-[var(--f500-ink)] text-sm font-semibold" style={{ fontFamily: 'var(--sf-display)' }}>
            <GraduationCap className="w-4 h-4" strokeWidth={1.75} />
            iStudyPro
          </Link>
          <div className="flex items-center gap-5 text-sm" style={{ fontFamily: 'var(--sf-text)' }}>
            <Link to="/legal" className="text-[var(--f500-ink-2)] hover:text-[var(--f500-ink)] transition-colors hidden sm:inline">Legal & DPA</Link>
            <a href="mailto:trust@istudypro.net" className="inline-flex items-center gap-1.5 text-[var(--f500-blue)] hover:text-[var(--f500-blue-hover)] transition-colors">
              <Mail className="w-3.5 h-3.5" /> Contact Trust
            </a>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="border-b border-[var(--f500-line)]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-20 lg:py-28">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-xs text-[var(--f500-ink-2)] hover:text-[var(--f500-ink)] mb-8 transition-colors"
            style={{ fontFamily: 'var(--sf-text)' }}
          >
            <ArrowLeft className="w-3.5 h-3.5" /> Back to Home
          </Link>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end">
            <div className="lg:col-span-8">
              <p className="f500-eyebrow mb-6">— For Educators / Trust & Compliance</p>
              <motion.h1
                initial="hidden"
                animate="visible"
                variants={fadeIn}
                transition={{ duration: 0.6 }}
                className="f500-display text-5xl sm:text-6xl lg:text-7xl"
              >
                Supporting your<br />students. Respecting<br />your IP.
              </motion.h1>
            </div>
            <div className="lg:col-span-4 lg:pl-8 lg:border-l lg:border-[var(--f500-line)]">
              <p className="f500-body text-base">
                iStudyPro is an AI-powered study assistant — not a homework cheat. Built with strict consent, zero-training, and academic-integrity guardrails.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Principles grid */}
      <section className="border-b border-[var(--f500-line)] bg-[var(--f500-paper-2)]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-16">
          <div className="mb-10">
            <p className="f500-eyebrow mb-3">— Our Principles</p>
            <h2 className="f500-h1 text-4xl">Four guarantees we put in writing.</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-0 border border-[var(--f500-line)] bg-[var(--isp-page)]">
            {PRINCIPLES.map((p, i) => (
              <div
                key={p.num}
                className={`p-8 lg:p-10 ${i % 2 === 0 ? 'md:border-r' : ''} ${i < 2 ? 'border-b' : ''} border-[var(--f500-line)]`}
              >
                <div className="flex items-start gap-4">
                  <span className="f500-mono text-xs text-[var(--f500-ink-3)] mt-1">{p.num}</span>
                  <div className="flex-1">
                    <p.icon className="w-5 h-5 text-[var(--f500-ink)] mb-3" strokeWidth={1.75} />
                    <h3 className="f500-h3 text-xl mb-2">{p.title}</h3>
                    <p className="f500-body text-sm">{p.body}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="border-b border-[var(--f500-line)]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-12 py-16">
          <div className="mb-10">
            <p className="f500-eyebrow mb-3">— Common Questions</p>
            <h2 className="f500-h1 text-4xl">For lecturers & admins.</h2>
          </div>
          <div className="border-t border-[var(--f500-line)]">
            {FAQS.map((faq, i) => {
              const isOpen = openFaq === i;
              return (
                <div key={i} className="border-b border-[var(--f500-line)]" data-testid={`educators-faq-${i}`}>
                  <button
                    onClick={() => setOpenFaq(isOpen ? null : i)}
                    className="w-full flex items-center justify-between gap-4 py-5 text-left hover:bg-[var(--f500-paper-2)] transition-colors px-2"
                  >
                    <div className="flex items-baseline gap-4 flex-1">
                      <span className="f500-mono text-xs text-[var(--f500-ink-3)] flex-shrink-0">0{i + 1}</span>
                      <span className="f500-h3 text-base sm:text-lg" style={{ fontFamily: 'var(--sf-display)' }}>{faq.q}</span>
                    </div>
                    <ChevronDown
                      className={`w-4 h-4 text-[var(--f500-ink-2)] flex-shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`}
                      strokeWidth={1.75}
                    />
                  </button>
                  {isOpen && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      transition={{ duration: 0.2 }}
                      className="px-2 pb-5"
                    >
                      <div className="pl-10 max-w-3xl">
                        <p className="f500-body text-sm">{faq.a}</p>
                      </div>
                    </motion.div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[var(--f500-ink)] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-16 lg:py-20">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-6 items-end">
            <div className="md:col-span-8">
              <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-white/60 mb-4">— Trust Center / DPA</p>
              <h2 className="f500-h1 text-white text-3xl sm:text-5xl">
                Need a signed DPA, audit log, or<br />consent-record export?
              </h2>
              <p className="text-white/70 text-base mt-4 max-w-2xl" style={{ fontFamily: 'var(--sf-text)' }}>
                Our compliance team replies within one business day. We support GDPR, FERPA, and EU/US data-residency requirements.
              </p>
            </div>
            <div className="md:col-span-4 md:text-right space-y-3">
              <a href="mailto:trust@istudypro.net" className="f500-btn f500-btn-inverse w-full md:w-auto" data-testid="educators-cta-trust">
                Email Trust Center <ArrowRight className="w-4 h-4" />
              </a>
              <Link to="/legal" className="block text-xs text-white/70 hover:text-white transition-colors" data-testid="educators-legal-link">
                Read Privacy Policy & DPA →
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[var(--isp-page)]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-8 text-center">
          <p className="text-xs text-[var(--f500-ink-3)]" style={{ fontFamily: 'var(--sf-text)' }}>
            © 2026 iStudyPro. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}
